'use client'

import { Menu } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useMobileNav } from '@/store/nav'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SubredditMenu } from '@/components/menu/subreddit-menu'
import { SiteMeta } from '@/components/menu/site-meta'
import { Separator } from '@/components/ui/separator'
import { useSubredditStore } from '@/store/subreddits'
import Link from 'next/link'
import { useTheme } from 'next-themes'
import { SettingsMenu } from '@/components/menu/settings-menu'
import { useMounted } from '@/hooks/use-mounted'

export function AppHeader() {
  const { openNav, isOpen, closeNav } = useMobileNav()
  const { theme, systemTheme } = useTheme()
  const mounted = useMounted()

  const currentTheme = theme === 'system' ? systemTheme : theme
  const isDark = currentTheme === 'dark'

  const { hydrated, getTopSubreddit } = useSubredditStore()
  const top = hydrated ? getTopSubreddit()?.name ?? 'all' : 'all'
  const logoTarget = `/r/${top}`

  if (!mounted) return null

  return (
    <header className='h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 relative md:sticky top-0 left-0 right-0 z-20'>
      <div className='h-full mx-auto px-4 flex items-center justify-between'>
        <Link href={logoTarget} className='flex items-center gap-2'>
          {/* prettier-ignore */}
          <svg width="105" height="32" xmlns="http://www.w3.org/2000/svg"><title>SkimIt</title><path d="M11.426 31.993H.418V26.14l1.784-2.946C.57 22.853.04 21.643.04 20.057v-6.459c0-.717.152-1.284.417-1.775L1.52 9.821c.38-1.7 1.746-2.568 3.492-2.568h11.235l.038 6.043-1.29 2.002c1.29.416 2.049 1.549 2.049 3.362v5.892c0 1.247-.38 3.022-.76 3.362l-1.29 2.002c-.683 1.246-2.05 2.077-3.567 2.077Zm-8.844-8.045v4.57h10.362c1.822 0 3.34-1.397 3.34-3.21V18.66c0-1.813-.911-2.757-2.391-2.757H7.099V12.54h8.426l-.038-4.533H5.01c-1.594 0-2.809.945-2.809 2.758v6.459c0 1.888 1.253 2.908 2.847 2.908h6.414v3.815H2.582Zm23.684 8.045h-6.263V3.023L21.408.075h6.49v13.22l2.923-6.043h6.528l-3.796 7.743c2.202.378 2.771 1.813 2.771 4.344v9.934l-1.632 2.72h-5.617V23.72h-1.177v5.553l-1.632 2.72ZM22.166.832v27.686h4.973v-8.272h4.1v8.272h4.326V19.34c0-2.455-.683-3.702-3.15-3.702l3.72-7.63h-4.859l-3.682 7.63h-.455V.832h-4.972Zm24.027 31.161h-6.15V10.2l1.026-1.926c-.456-.491-.684-1.134-.684-1.889 0-.491.114-.982.342-1.398l.076-.188 1.063-1.927C42.169 1.55 43.308.53 44.788.53c1.708 0 2.999 1.36 2.999 3.022 0 .831-.304 1.587-.835 2.115l-.873 1.587h1.746v22.02l-1.632 2.72ZM44.788 5.78c1.253 0 2.24-.982 2.24-2.229 0-1.246-.987-2.266-2.24-2.266-1.252 0-2.24 1.02-2.24 2.266 0 1.247.988 2.229 2.24 2.229Zm-2.58 2.228v20.51h4.858V8.008h-4.859Zm14.688 23.985h-6.073V10.2l1.405-2.946h20.8c2.238 0 4.098 1.7 4.098 3.966v18.055l-1.632 2.72h-6.11V16.09h-1.557v13.183l-1.632 2.72h-6.11V16.09h-1.557v13.183l-1.632 2.72Zm-3.91-23.985v20.51h4.783V12.616h4.479v15.902h4.82V12.616h4.479v15.902h4.82v-17.3c0-1.812-1.518-3.21-3.34-3.21h-20.04Zm33.325 23.985h-6.148V10.2l1.024-1.926c-.455-.491-.683-1.134-.683-1.889 0-.491.114-.982.342-1.398l.076-.188 1.063-1.927C82.288 1.55 83.427.53 84.907.53c1.708 0 2.999 1.36 2.999 3.022 0 .831-.304 1.587-.835 2.115l-.873 1.587h1.746v22.02l-1.633 2.72ZM84.907 5.78c1.253 0 2.24-.982 2.24-2.229 0-1.246-.987-2.266-2.24-2.266-1.252 0-2.24 1.02-2.24 2.266 0 1.247.988 2.229 2.24 2.229Zm-2.58 2.228v20.51h4.857V8.008h-4.858Zm20.799 23.985h-5.96c-3.188 0-3.87-.906-3.795-3.928l.038-12.238-3.226.038v-5.666l1.404-2.946h1.898v-4.23L94.889.075h5.997l.038 7.177h3.986v5.892l-1.632 2.72h-2.43v7.592l3.91.038v5.779l-1.632 2.72Zm-4.441-3.475h5.314V24.25l-3.91-.038v-11.86l4.061.038V8.008h-3.985l-.038-7.176h-4.479v7.176h-3.302v4.382l3.227-.038-.038 12.88c-.038 2.53.531 3.286 3.15 3.286Z" fill={isDark ? "#FAFAFA" : "#0A0A0A"} /></svg>
        </Link>

        <div className='inline-flex items-center gap-4'>
          <Button
            variant='secondary'
            className='md:hidden'
            onClick={openNav}
            aria-label='Open menu'
          >
            <Menu className='h-10 w-10' />
          </Button>

          <Sheet
            open={isOpen}
            onOpenChange={(open: boolean) => !open && closeNav()}
          >
            <SheetContent side='right' className='w-[300px] sm:w-[400px] p-0'>
              <div className='flex flex-col h-full p-4'>
                <SheetHeader className='border-b mb-4 pb-2'>
                  <SheetTitle className='text-left'>Menu</SheetTitle>
                </SheetHeader>

                <SubredditMenu />

                <Separator className='my-4' />

                <SiteMeta />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
